import { action, computed } from 'mobx'
import { type Job } from '@/features/jobs/Job'
import { links } from '@/utils/link'
import { type NotificationItemViewModel } from './NotificationItemViewModel'
import type { ThreadItemEntry } from '@/features/notifications/components/NotificationsPage'

/**
 * The notifications about one job, or a single notification about no job.
 */
export class NotificationThreadViewModel {
  constructor(
    /**
     * The job's id, or for a notification about no job, one derived from the
     * notification. Stable while the thread gains notifications.
     */
    readonly key: string,
    readonly jobId: string | null,
    private readonly _getItems: () => NotificationItemViewModel[],
    private readonly _job: () => Job | undefined,
  ) {}

  /**
   * Newest first, so `items[0]` is `latest`.
   */
  @computed
  get items() {
    return this._getItems()
  }

  /**
   * The newest notification.
   */
  @computed
  get latest() {
    return this.items[0]
  }

  /**
   * The notifications before the newest one.
   */
  @computed
  get earlier(): ThreadItemEntry[] {
    return this.items.slice(1).map((item) => ({
      id: item.id,
      isRead: item.isRead,
      plainText: item.plainText,
      dispatchedAt: item.dispatchedAt,
    }))
  }

  /**
   * The job the thread is about, once something has loaded it into the job
   * store.
   */
  @computed
  get job(): Job | undefined {
    return this._job()
  }

  /**
   * The job's name, once loaded.
   */
  @computed
  get jobDisplayName() {
    return this.job ? this.job.displayName : null
  }

  /**
   * Whether any notification in the thread is unread.
   */
  @computed
  get isUnread() {
    return this.items.some((item) => !item.isRead)
  }

  /**
   * Where opening the thread leads. Notifications about anything other than a
   * job still leave the notifications page.
   */
  @computed
  get href() {
    return this.jobId ? links.notifications(this.jobId) : this.latest.actionUrl
  }

  /**
   * The client the job is for, unless the viewer is that client; otherwise
   * the workspace.
   */
  @computed
  get context() {
    const clientName = this.job?.championClient?.member.displayName
    const viewerIsClient = this.job?.currentUserCollaborator?.type === 'Client'
    return clientName && !viewerIsClient
      ? clientName
      : this.latest.workspaceName
  }

  /**
   * Marks every notification in the thread as read.
   */
  @action.bound
  markAsRead() {
    this.items
      .filter((item) => !item.isRead)
      .forEach((item) => item.markAsRead())
  }
}
